import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Star, User, Phone } from 'lucide-react';

function JoinTalentShow() {
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({
    stage_name: '',
    phone: '',
    talent1: '',
    talent2: '',
    talent3: '',
    performance_description: '',
    social_media_description: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, { credentials: 'include' });
      
      if (!response.ok) {
        toast.info('Please login or register first to join the talent show');
        navigate('/login');
        return;
      }
      
      const data = await response.json();
      setUser(data);
      
      // Pre-fill if user already has data
      if (data.is_artist) {
        toast.info('You are already registered as an artist!');
        setFormData({
          stage_name: data.stage_name || '',
          phone: data.phone || '',
          talent1: data.talents?.[0] || '',
          talent2: data.talents?.[1] || '',
          talent3: data.talents?.[2] || '',
          performance_description: data.performance_description || '',
          social_media_description: data.social_media_description || ''
        });
      }
    } catch (error) {
      toast.info('Please login or register first to join the talent show');
      navigate('/login');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const talents = [formData.talent1, formData.talent2, formData.talent3].filter(t => t.trim());

      const artistData = {
        stage_name: formData.stage_name,
        phone: formData.phone,
        talents: talents,
        performance_description: formData.performance_description,
        social_media_description: formData.social_media_description
      };

      const response = await fetch(`${API}/users/register-artist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(artistData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to register as artist');
      }

      toast.success('Successfully joined the talent show!');
      // Redirect to earn-votes page after registration
      navigate('/earn-votes');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen pb-24">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          ← Back
        </Button>

        <div className="mb-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-4 tracking-tight text-glow">Join Talent Show</h1>
          <p className="text-xl text-muted-foreground">Showcase your talent to the world and compete for amazing prizes!</p>
        </div>

        <Card className="glass-panel p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="text-sm font-medium mb-2 block">Stage Name *</label>
              <div className="relative">
                <User className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                <Input
                  type="text"
                  name="stage_name"
                  value={formData.stage_name}
                  onChange={handleChange}
                  className="pl-10 bg-white/5 border-white/10 focus:border-primary h-12 rounded-xl"
                  placeholder="Your stage/artist name"
                  required
                  data-testid="stage-name-input"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Phone Number *</label>
              <div className="relative">
                <Phone className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="pl-10 bg-white/5 border-white/10 focus:border-primary h-12 rounded-xl"
                  placeholder="+27 123 456 789"
                  required
                  data-testid="phone-input"
                />
              </div>
            </div>

            <div className="space-y-4">
              <label className="text-sm font-medium block">Your Talents * (At least one required)</label>
              
              <div className="relative">
                <Star className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                <Input
                  type="text"
                  name="talent1"
                  value={formData.talent1}
                  onChange={handleChange}
                  className="pl-10 bg-white/5 border-white/10 focus:border-primary h-12 rounded-xl"
                  placeholder="Talent 1 (e.g., Singing)"
                  required
                  data-testid="talent1-input"
                />
              </div>

              <div className="relative">
                <Star className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                <Input
                  type="text"
                  name="talent2"
                  value={formData.talent2}
                  onChange={handleChange}
                  className="pl-10 bg-white/5 border-white/10 focus:border-primary h-12 rounded-xl"
                  placeholder="Talent 2 (e.g., Dancing)"
                  data-testid="talent2-input"
                />
              </div>

              <div className="relative">
                <Star className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                <Input
                  type="text"
                  name="talent3"
                  value={formData.talent3}
                  onChange={handleChange}
                  className="pl-10 bg-white/5 border-white/10 focus:border-primary h-12 rounded-xl"
                  placeholder="Talent 3 (e.g., Acting)"
                  data-testid="talent3-input"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Performance Description *</label>
              <Textarea
                name="performance_description"
                value={formData.performance_description}
                onChange={handleChange}
                className="bg-white/5 border-white/10 focus:border-primary rounded-xl min-h-[120px]"
                placeholder="Describe your performance style, what makes you unique, your experience..."
                required
                data-testid="performance-input"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Social Media Description</label>
              <Textarea
                name="social_media_description"
                value={formData.social_media_description}
                onChange={handleChange}
                className="bg-white/5 border-white/10 focus:border-primary rounded-xl min-h-[100px]"
                placeholder="Your social media handles, links to your work, portfolio..."
                data-testid="social-media-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-secondary text-black hover:brightness-110 rounded-full py-6 font-bold text-lg"
              disabled={isLoading}
              data-testid="join-talent-show-submit-btn"
            >
              <Star className="mr-2 h-5 w-5" />
              {isLoading ? 'Joining...' : user?.is_artist ? 'Update Registration' : 'Join Talent Show'}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}

export default JoinTalentShow;